import { type ReactNode } from 'react';
import { type ColumnDef, type Table } from '@tanstack/react-table';

// Legacy Column type from old DataTable - kept for backward compatibility
export interface LegacyColumn<T> {
  key: string;
  header: string;
  sortable?: boolean;
  render?: (item: T) => ReactNode;
  className?: string;
  /**
   * The scalar to sort, filter and export by, when `key` does not name one.
   * `render` controls what is drawn; this controls what the table reasons
   * about. Needed whenever `key` is a synthetic id or names a relation.
   */
  accessorFn?: (item: T) => string | number | null | undefined;
  /**
   * Where this cell's value leads — the record's own page.
   *
   * Set this on the column that carries the record's identity (its SKU, PO
   * number, name) and the cell becomes a real link. That is worth more than it
   * sounds: a clickable row is invisible to the keyboard, announces nothing to
   * a screen reader, and cannot be opened in a new tab. Fifteen lists relied on
   * one, and on five of them there was no other way in at all.
   *
   * Rows may stay clickable as a mouse convenience; the link is what makes the
   * record reachable.
   */
  linkTo?: (item: T) => string | null | undefined;
}

// Filter option for faceted filters
export interface FacetedFilterOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  count?: number;
}

// Export format
export type ExportFormat = 'csv';

// Row action definition
export interface RowAction<T> {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: (row: T) => void;
  disabled?: (row: T) => boolean;
  destructive?: boolean;
  separator?: boolean;
}

// DataTable props for the batteries-included wrapper
export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[] | LegacyColumn<T>[];
  data: T[];
  loading?: boolean;
  // Features
  searchable?: boolean;
  searchPlaceholder?: string;
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  exportable?: boolean;
  exportFilename?: string;
  columnToggle?: boolean;
  resizable?: boolean;
  stickyHeader?: boolean;
  maxHeight?: string;
  // Selection
  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  getRowId?: (row: T) => string;
  // Row interaction
  onRowClick?: (row: T) => void;
  /**
   * Per-row emphasis, e.g. flagging the cheapest quote in a comparison. The
   * returned classes are applied after the base row styling, so they win.
   */
  rowClassName?: (row: T) => string | undefined;
  renderExpandedRow?: (row: T) => ReactNode;
  /**
   * The page's own filters, rendered inside the table's control row.
   *
   * Export and column-toggle sit at the right of that row. Without this the
   * page renders its filters in a row of their own directly above, and the
   * table renders a second row holding two right-aligned buttons and an empty
   * half — two control rows doing one row's work, on twenty-two pages.
   */
  toolbar?: ReactNode;
  // Empty/Loading states
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  // Styling
  className?: string;
  caption?: string;
  /** Use Figma Design System typography (default: false) */
  // Faceted filters config
  facetedFilters?: {
    columnId: string;
    title: string;
    options: FacetedFilterOption[];
  }[];
  // Server-side pagination support
  manualPagination?: boolean;
  pageCount?: number;
  onPaginationChange?: (pageIndex: number, pageSize: number) => void;
}

// Context type for composition mode
export interface DataTableContextType<T> {
  table: Table<T>;
}

// Check if columns are legacy format
export function isLegacyColumns<T>(
  columns: ColumnDef<T, unknown>[] | LegacyColumn<T>[]
): columns is LegacyColumn<T>[] {
  return columns.length > 0 && 'key' in columns[0];
}

// Adapter to convert legacy Column<T> to TanStack ColumnDef<T>
// This is imported by DataTable.tsx to support backward compatibility
